import React from 'react';
import { MenuItem } from '../../../services/restaurantService';
import { DEFAULT_IMAGES, getMenuItemImageUrl } from '../../../constants/images';
import {
  getEffectiveDisplayPrice,
  getStrikeThroughPrice,
} from '../../../utils/menuPricing';
import { IconCheck, IconDietaryMark, IconPlus, stripTagEmoji } from '../../RestaurantMenu/MenuListingIcons';
import { IconFlame } from '../../RestaurantHome/RestaurantDetailIcons';
import './MenuRecommendationsStrip.css';

export type MenuRecommendationsVariant = 'featured' | 'compact';

export interface MenuRecommendationsStripProps {
  title: string;
  subtitle?: string;
  items: MenuItem[];
  appName: string;
  restroId: number;
  variant?: MenuRecommendationsVariant;
  cartMenuIds?: Set<number>;
  formatPrice: (price: number | undefined | null) => string;
  onNavigate: (item: MenuItem) => void;
  onAdd?: (item: MenuItem, e: React.MouseEvent) => void;
  addingMenuId?: number | null;
  className?: string;
  showImages?: boolean;
}

function getFirstMenuTag(item: MenuItem): string | null {
  if (!item.tags?.length) return null;
  const label = stripTagEmoji(item.tags[0]);
  return label || null;
}

function renderDiscountPct(item: MenuItem): number {
  const price = getEffectiveDisplayPrice(item);
  const strike = getStrikeThroughPrice(item);
  if (strike != null && strike > price) {
    return Math.round(((strike - price) / strike) * 100);
  }
  if (item.discountPercent != null && item.discountPercent > 0) {
    return Math.round(item.discountPercent);
  }
  return 0;
}

const MenuRecommendationsStrip: React.FC<MenuRecommendationsStripProps> = ({
  title,
  subtitle,
  items,
  restroId,
  variant = 'compact',
  cartMenuIds,
  formatPrice,
  onNavigate,
  onAdd,
  addingMenuId,
  className,
  showImages = true,
}) => {
  if (!items.length) return null;

  const getImageUrl = (imageName: string | undefined, menuId: number): string => {
    if (!restroId || !menuId || !imageName || imageName === 'default') {
      return DEFAULT_IMAGES.MENU_ITEM;
    }
    return getMenuItemImageUrl(restroId, menuId, imageName);
  };

  if (variant === 'featured') {
    return (
      <div className={`menu-rec-strip menu-rec-strip--featured${className ? ` ${className}` : ''}`}>
        <div className="menu-rec-header">
          <div className="menu-rec-header-left">
            <span className="menu-rec-flame" aria-hidden="true">
              <IconFlame size={22} />
            </span>
            <div>
              <h2 className="menu-rec-heading">{title}</h2>
              {subtitle && <p className="menu-rec-sub">{subtitle}</p>}
            </div>
          </div>
        </div>
        <div className="menu-rec-featured-list">
          {items.map((item) => {
            const price = getEffectiveDisplayPrice(item);
            const strike = getStrikeThroughPrice(item);
            const pct = renderDiscountPct(item);
            const inCart = cartMenuIds?.has(item.id) ?? false;
            const firstTag = getFirstMenuTag(item);
            return (
              <div key={item.id} className={`menu-rec-featured-card${showImages ? '' : ' menu-rec-featured-card--no-image'}`}>
                <button
                  type="button"
                  className="menu-rec-featured-main"
                  onClick={() => onNavigate(item)}
                >
                  {showImages && (
                    <div className="menu-rec-featured-img-wrap">
                      <img
                        className="menu-rec-featured-img"
                        src={getImageUrl(item.imageBig, item.id)}
                        alt={item.item}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = DEFAULT_IMAGES.MENU_ITEM;
                        }}
                      />
                      <span
                        className="menu-rec-featured-diet"
                        aria-label={item.itemType === 'nonveg' ? 'Non-vegetarian' : 'Vegetarian'}
                      >
                        <IconDietaryMark nonVeg={item.itemType === 'nonveg'} size={15} />
                      </span>
                      {firstTag && <span className="menu-rec-featured-ribbon">{firstTag}</span>}
                    </div>
                  )}
                  <div className="menu-rec-featured-info">
                    <div className="menu-rec-featured-name">{item.item}</div>
                    {item.description && <div className="menu-rec-featured-desc">{item.description}</div>}
                    <div className="menu-rec-featured-price-row">
                      <span className="menu-rec-featured-price">{formatPrice(price)}</span>
                      {strike != null && strike > price && (
                        <span className="menu-rec-featured-orig">{formatPrice(strike)}</span>
                      )}
                      {pct > 0 && <span className="menu-rec-featured-badge">{pct}% off</span>}
                    </div>
                  </div>
                </button>
                {onAdd && (
                  <div
                    className="menu-rec-featured-action"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`menu-rec-featured-add-btn${inCart ? ' added' : ''}`}
                      aria-label={inCart ? `${item.item} in cart` : `Add ${item.item} to cart`}
                      disabled={addingMenuId === item.id}
                      onClick={(e) => onAdd(item, e)}
                    >
                      {inCart ? <IconCheck size={16} /> : <IconPlus size={18} />}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={`menu-rec-strip menu-rec-strip--compact${className ? ` ${className}` : ''}`}>
      <h3 className="menu-rec-compact-title">
        <IconFlame size={14} className="menu-rec-compact-title-icon" aria-hidden />
        {title}
      </h3>
      <div className="menu-rec-compact-list">
        {items.map((item) => {
          const price = getEffectiveDisplayPrice(item);
          return (
            <div key={item.id} className={`menu-rec-compact-item${showImages ? '' : ' menu-rec-compact-item--no-image'}`}>
              <button
                type="button"
                className="menu-rec-compact-item-main"
                onClick={() => onNavigate(item)}
              >
                {showImages && (
                  <img
                    className="menu-rec-compact-img"
                    src={getImageUrl(item.imageBig, item.id)}
                    alt={item.item}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_IMAGES.MENU_ITEM;
                    }}
                  />
                )}
                <div className="menu-rec-compact-info">
                  <p className="menu-rec-compact-name">{item.item}</p>
                  <p className="menu-rec-compact-price">{formatPrice(price)}</p>
                </div>
              </button>
              {onAdd && (
                <button
                  type="button"
                  className="menu-rec-compact-add"
                  disabled={addingMenuId === item.id}
                  onClick={(e) => onAdd(item, e)}
                >
                  + Add
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MenuRecommendationsStrip;
